// src/stores/settings/cron-schedule.store.js
import { defineStore } from "pinia";
import { ref } from "vue";
import CronScheduleService from "@/services/settings/cron-schedule.service";
import { toast } from "@/utils/toast";

export const useCronScheduleStore = defineStore("cronSchedules", () => {
  const rows = ref([]);
  const loading = ref(false);

  // 🔹 holds the schedule currently being edited
  const current = ref(null);

  function setCurrent(item) {
    current.value = item ? { ...item } : null;
  }

  function clearCurrent() {
    current.value = null;
  }

  async function fetchList() {
    loading.value = true;
    try {
      const res = await CronScheduleService.getAll();
      rows.value = res.data || [];
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to load cron schedules",
        "error"
      );
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await CronScheduleService.create(payload);
      toast("Cron schedule created", "success");
      await fetchList();
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to create cron schedule",
        "error"
      );
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await CronScheduleService.update(id, payload);
      toast("Cron schedule updated", "success");
      await fetchList();
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to update cron schedule",
        "error"
      );
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function toggle(id) {
    loading.value = true;
    try {
      await CronScheduleService.toggle(id);
      toast("Cron schedule status updated", "success");
      await fetchList();
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to toggle cron schedule",
        "error"
      );
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    if (!ids?.length) return;
    loading.value = true;
    try {
      await CronScheduleService.bulkDelete(ids);
      toast("Cron schedules deleted", "success");
      await fetchList();
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to delete cron schedules",
        "error"
      );
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    rows,
    loading,
    current,
    fetchList,
    create,
    update,
    toggle,
    bulkDelete,
    setCurrent,
    clearCurrent,
  };
});
